"use client";
import React from "react";
import { Caveat } from "next/font/google";
import { motion } from "framer-motion";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

/* ─────────────────────────────────────────────
   1. Make your classes shine (Yoga & Pilates Studio)
   ───────────────────────────────────────────── */
export function YogaMakeClassesShineSection({ section }) {
  const yogaClasses = [
    {
      title: "Morning flow",
      desc: "Dynamic flows that energize and invigorate, perfect for a summer morning.",
      image: "/odoo/industries/yoga/yoga-morning-flow.jpg",
      aspectRatio: "aspect-square"
    },
    {
      title: "Pilates Reformer",
      desc: "Dynamic class building strength, control, and fluid movement while enhancing flexibility and core stability.",
      image: "/odoo/industries/yoga/yoga-pilates-reformer.jpg",
      aspectRatio: "aspect-[4/5]"
    },
    {
      title: "Yoga Bliss",
      desc: "Soft, calming practices that bring tranquility and ease to your day.",
      image: "/odoo/industries/yoga/yoga-bliss.jpg",
      aspectRatio: "aspect-square"
    },
    {
      title: "Evening restorative",
      desc: "A gentle blend of restorative poses and breathwork, ideal for relaxation and stress relief.",
      image: "/odoo/industries/yoga/yoga-evening-restorative.jpg",
      aspectRatio: "aspect-[4/5]"
    }
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-10 sm:mb-14"
        >
          <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
            <span>Make your </span>
            <span className="relative inline-block px-1">
              classes
              <svg 
                className="absolute -bottom-1 left-0 w-full h-3.5 text-amber-400 overflow-visible" 
                viewBox="0 0 100 14" 
                fill="none"
              >
                <path 
                  d="M 0 6 L 100 6 M 5 11 L 95 11" 
                  stroke="#FBBF24" 
                  strokeWidth="4" 
                  strokeLinecap="round" 
                />
              </svg>
            </span>
            <span> shine</span>
          </h2>
        </motion.div>

        {/* 2-Column Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 text-slate-300 text-base sm:text-lg leading-relaxed mb-12 sm:mb-16 max-w-[1400px] w-full mx-auto font-normal"
        >
          <div>
            <p>
              <strong className="text-white font-bold">Create your website</strong> using Odoo's AI-based builder, page templates, 3+ million free photos, drag-and-drop elements, and SEO optimization tools. No coding skills required.
            </p>
          </div>

          <div>
            <p>
              <strong className="text-white font-bold">Showcase your classes and membership types,</strong> offer online bookings and payments, and promote events such as special workshops.
            </p>
          </div>
        </motion.div>

        {/* Overlapping Mockup Display Container */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="relative max-w-[1400px] mx-auto pt-4"
        >
          {/* Combined Graphic Image Display matching reference image */}
          <div className="w-full relative overflow-hidden rounded-2xl sm:rounded-3xl">
            <img 
              src="/odoo/industries/yoga/yoga-classes-mockup.png" 
              alt="Make your classes shine - Yoga & Pilates Studio Website" 
              className="w-full h-auto object-contain rounded-2xl sm:rounded-3xl" 
            />
          </div>

        </motion.div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   2. Balanced bookings (Yoga & Pilates Studio)
   ───────────────────────────────────────────── */
export function YogaBalancedBookingsSection({ section }) {
  const publishedClasses = [
    { name: "Morning Flow", duration: "1 hour Duration", room: "Room1 (👤 16)", upcoming: "10 Bookings Upcoming", total: "15 Bookings Total" },
    { name: "Yoga Bliss", duration: "1 hour Duration", room: "Room2 (👤 25)", upcoming: "5 Bookings Upcoming", total: "27 Bookings Total" },
    { name: "Pilates Reformer", duration: "1 hour Duration", room: "Room1 (👤 16)", upcoming: "22 Bookings Upcoming", total: "46 Bookings Total" },
    { name: "Evening Restorative", duration: "1 hour Duration", room: "Room2 (👤 25)", upcoming: "32 Bookings Upcoming", total: "41 Bookings Total" },
    { name: "Classic Pilates", duration: "1 hour Duration", room: "Room1 (👤 16)", upcoming: "8 Bookings Upcoming", total: "18 Bookings Total" }
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Left Column: Vertical Mobile Booking Picker Mockup */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-4 relative flex justify-center"
          >
            {/* Mobile App Window */}
            <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 p-4 sm:p-5 text-slate-800 font-sans text-xs w-full max-w-sm">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-3 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">☰</span>
                  <div className="w-5 h-5 rounded-full bg-rose-400 flex items-center justify-center text-white text-[10px]">🌸</div>
                </div>
                <div className="flex items-center gap-3 text-slate-500 text-xs">
                  <span>🛒</span>
                  <span>☰</span>
                </div>
              </div>

              {/* Subheader */}
              <div className="space-y-1 mb-4">
                <div className="text-[11px] text-slate-400 font-medium">← All Appointments</div>
                <h4 className="text-base font-bold text-slate-900">Reformer Pilates</h4>
              </div>

              {/* Calendar Picker Box */}
              <div className="border border-slate-200 rounded-xl p-3 bg-slate-50/50 mb-4">
                <div className="text-center font-bold text-slate-700 text-xs mb-2">September 2025</div>
                <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-medium text-slate-500 mb-1">
                  <span>Sun</span><span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold text-slate-700">
                  <span className="text-slate-300"></span><span className="py-1">1</span><span className="py-1">2</span><span className="py-1">3</span><span className="py-1">4</span><span className="py-1">5</span><span className="py-1">6</span>
                  <span className="py-1">7</span><span className="py-1">8</span><span className="py-1 bg-slate-800 text-white rounded-lg">9</span><span className="py-1">10</span><span className="py-1">11</span><span className="py-1">12</span><span className="py-1">13</span>
                  <span className="py-1">14</span><span className="py-1">15</span><span className="py-1">16</span><span className="py-1">17</span><span className="py-1">18</span><span className="py-1">19</span><span className="py-1">20</span>
                  <span className="py-1">21</span><span className="py-1">22</span><span className="py-1">23</span><span className="py-1">24</span><span className="py-1">25</span><span className="py-1">26</span><span className="py-1">27</span>
                  <span className="py-1">28</span><span className="py-1">29</span><span className="py-1">30</span>
                </div>
              </div>

              {/* Time Slots */}
              <div className="space-y-2 mb-4">
                <div className="text-[11px] font-medium text-slate-600">Select a time</div>
                <div className="grid grid-cols-2 gap-2 text-center text-[11px] font-semibold text-slate-700">
                  <div className="border border-slate-200 rounded-lg py-2 bg-white">8:00 AM</div>
                  <div className="border border-slate-200 rounded-lg py-2 bg-white">9:00 AM</div>
                  <div className="border border-slate-200 rounded-lg py-2 bg-white">10:00 AM</div>
                  <div className="border border-slate-200 rounded-lg py-2 bg-white">6:00 PM</div>
                  <div className="border border-slate-200 rounded-lg py-2 bg-white col-span-1">7:00 PM</div>
                </div>
              </div>

              {/* Pricing & Location */}
              <div className="border-t border-slate-100 pt-3 text-[11px] space-y-1.5 text-slate-600">
                <div className="font-bold text-slate-900 flex items-center gap-1.5">
                  💳 <span>25.00 € per session</span>
                </div>
                <div className="flex items-start gap-1.5 text-slate-500">
                  📍 
                  <div>
                    <div className="font-semibold text-slate-800">Soulflow</div>
                    <div>Rue des Bourlottes 9, 1367 Ramillies, Belgium</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Title, Paragraphs & Appointments Table Board */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-8 space-y-6 sm:space-y-8"
          >
            {/* Title with Dotted Green Highlight */}
            <div>
              <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.15]`}>
                <span>Balanced </span>
                <span className="relative inline-block px-1">
                  bookings
                  <svg 
                    className="absolute -bottom-1 left-0 w-full h-3.5 text-teal-400 overflow-visible" 
                    viewBox="0 0 100 14" 
                    fill="none"
                  >
                    <path 
                      d="M 0 8 C 20 2, 40 14, 60 4 C 80 14, 100 6, 100 6" 
                      stroke="#14B8A6" 
                      strokeWidth="5" 
                      strokeDasharray="4 6" 
                      strokeLinecap="round" 
                    />
                  </svg>
                </span>
              </h2>
            </div>

            {/* Paragraphs */}
            <div className="space-y-6 text-slate-300 text-base sm:text-lg lg:text-xl leading-relaxed font-normal">
              <p>
                <strong className="text-white font-bold">Bookings made easy.</strong> The customer portal allows your clients to book classes with a few clicks.
              </p>
              <p>
                <strong className="text-white font-bold">Resource reservations.</strong> Instructors, studio rooms, and machines are all included in Odoo's Appointments for seamless scheduling.
              </p>
            </div>

            {/* Appointments Management Table Board */}
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-slate-200 overflow-hidden p-4 sm:p-6 text-slate-800 font-sans text-xs space-y-3 relative">
              {publishedClasses.map((item, idx) => (
                <div 
                  key={idx}
                  className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3 last:border-0 last:pb-0 relative pr-16"
                >
                  <div className="min-w-[140px]">
                    <div className="font-bold text-slate-900 text-sm">{item.name}</div>
                    <div className="text-[11px] text-slate-400">{item.duration}</div>
                  </div>

                  <div className="flex items-center gap-1 bg-purple-50 text-purple-900 px-2.5 py-1 rounded-lg text-[11px] font-medium border border-purple-100">
                    <span className="w-4 h-4 rounded bg-purple-600 text-white font-bold text-[10px] flex items-center justify-center">R</span>
                    <span>{item.room}</span>
                  </div>

                  <div className="text-right text-[11px]">
                    <div className="font-bold text-slate-900">{item.upcoming}</div>
                    <div className="text-slate-400">{item.total}</div>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <button className="px-2.5 py-1 bg-slate-700 text-white font-semibold rounded text-[10px]">Share</button>
                    <button className="px-2.5 py-1 bg-slate-100 text-slate-700 font-medium rounded text-[10px] border border-slate-200">Configure</button>
                  </div>

                  {/* Published Ribbon Badge */}
                  <div className="absolute top-0 right-0 overflow-hidden w-16 h-16 pointer-events-none">
                    <div className="bg-emerald-500 text-white text-[8px] font-bold py-0.5 px-3 uppercase tracking-wider transform rotate-45 translate-x-4 translate-y-2 text-center shadow-xs">
                      PUBLISHED
                    </div>
                  </div>
                </div>
              ))}
            </div>

          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   3. Find your sales flow (Yoga & Pilates Studio)
   ───────────────────────────────────────────── */
export function YogaFindSalesFlowSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          
          {/* Left Column: Title & Copy */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 space-y-6 sm:space-y-8"
          >
            {/* Title with Salmon/Pink Marker Highlight */}
            <div>
              <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.15]`}>
                <span>Find your </span>
                <span className="relative inline-block px-1">
                  sales
                  <svg 
                    className="absolute -bottom-1 left-0 w-full h-4 text-rose-400 -z-10 overflow-visible" 
                    fill="none" 
                    viewBox="0 0 100 16"
                  >
                    <path 
                      d="M 0 8 L 100 8" 
                      stroke="#FB7185" 
                      strokeWidth="14" 
                      strokeLinecap="round" 
                      opacity="0.85" 
                    />
                  </svg>
                </span>
                <span> flow</span>
              </h2>
            </div>

            {/* Copy Paragraphs */}
            <div className="space-y-6 text-slate-300 text-base sm:text-lg lg:text-xl leading-relaxed font-normal">
              <p>
                <strong className="text-white font-bold">Sell single and multiple class packages,</strong> membership plans, 1-on-1 sessions, and more. Create stunning quotations and invoices in one click.
              </p>
              <p>
                <strong className="text-white font-bold">Hands-free subscriptions.</strong> Set up automated recurring invoicing for your clients, so you always get paid on time.
              </p>
            </div>
          </motion.div>

          {/* Right Column: Overlapping Invoice & Membership Catalog Display */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 relative flex justify-center"
          >
            <div className="w-full relative overflow-hidden rounded-2xl sm:rounded-3xl">
              <img 
                src="/odoo/industries/yoga/yoga-sales-flow-right-graphic.jpg" 
                alt="Find your sales flow - Invoicing & Subscriptions" 
                className="w-full h-auto object-contain rounded-2xl sm:rounded-3xl" 
              />
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   4. Reformed planning (Yoga & Pilates Studio)
   ───────────────────────────────────────────── */
export function YogaReformedPlanningSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          
          {/* Left Column: Planning Roster Calendar Display */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 relative flex justify-center"
          >
            <div className="w-full relative overflow-hidden rounded-2xl sm:rounded-3xl shadow-xl border border-slate-200/80 bg-white">
              <img 
                src="/odoo/industries/yoga/yoga-planning-left-graphic.jpg" 
                alt="Reformed planning - Shift Roster & Staff Scheduling" 
                className="w-full h-auto object-contain rounded-2xl sm:rounded-3xl" 
              />
            </div>
          </motion.div>

          {/* Right Column: Title & Copy */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 space-y-6 sm:space-y-8"
          >
            {/* Title with Sky-Blue Marker Highlight */}
            <div>
              <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.15]`}>
                <span>Reformed </span>
                <span className="relative inline-block px-1">
                  planning
                  <svg 
                    className="absolute -bottom-1 left-0 w-full h-4 text-sky-400 -z-10 overflow-visible" 
                    fill="none" 
                    viewBox="0 0 100 16"
                  >
                    <path 
                      d="M 0 8 L 100 8" 
                      stroke="#38BDF8" 
                      strokeWidth="14" 
                      strokeLinecap="round" 
                      opacity="0.85" 
                    />
                  </svg>
                </span>
              </h2>
            </div>

            {/* Copy Paragraph */}
            <div className="space-y-6 text-slate-300 text-base sm:text-lg lg:text-xl leading-relaxed font-normal">
              <p>
                <strong className="text-white font-bold">Seamless planning.</strong> Create single and recurring classes, keep track of resources, and empower your employees to fill in for each other if needed.
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

export default YogaMakeClassesShineSection;
